/* eslint-disable max-lines */
import { useCallback } from "react";
import { type models } from "jiffy-api";
import sendServerSideConversion from "common/utils/conversion-tracking";
import { MadeAddToCartDocument } from "graphql-api/operations";
import type { AddMadeToCartInput, PlacementArtworkPosition } from "graphql-api/types";
import type { DesignLabFormType } from "@design-lab/shared/types";
import { useIsSample, useDesignLabConfig } from "@design-lab/shared/lib/config";
import {
  trackAddToCartError,
  buildAddToCartAnalyticsData,
  sendAddToCartAnalytics
} from "@design-lab/shared/lib/analytics";
import { DimensionLoadingError } from "@design-lab/shared/lib/errors";
import { useCurrentComposition } from "@design-lab/entities/composition/model/useCurrentComposition";
import { useCompositionPricing, useCompositionsList } from "@design-lab/entities/composition/model";
import { useMutation } from "@design-lab/shared/graphql";
import { PLACEMENTS } from "@design-lab/shared/constants";
import { useAtomValue } from "jotai";
import {
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  isSessionFinished,
  pinningSessionsAtom,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";

export function useAddToCartSubmit() {
  const [addToCart] = useMutation(MadeAddToCartDocument);
  const { currentComposition } = useCurrentComposition();
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const { getVariantPrice } = useCompositionPricing();
  const { products, getCompositionById } = useCompositionsList();
  const pinningSessions = useAtomValue(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);

  const executeAddToCart = useCallback(
    async (data: DesignLabFormType) => {
      const variants = data.compositions.addedCompositions.flatMap(composition =>
        Object.entries(composition.variants)
          .filter(([_, quantity]) => typeof quantity === "number" && quantity > 0)
          .map(([variantId, quantity]) => ({
            variantId,
            quantity: quantity as number
          }))
      );

      // Fill placements array
      const placementsToAddToCart = PLACEMENTS.filter(
        placement => placement === "back" || data.artwork.selectedFrontPlacement === placement
      );

      const placementsInput = placementsToAddToCart
        .map(placement => {
          const placementData = data.artwork[placement];

          if (!placementData?.artworkId || !placementData?.size) {
            return null;
          }

          const placementInfo = data.artwork.info[placement === "back" ? "back" : "front"];
          const position = placement === "frontLeftChest" ? "front_left_chest" : placement;

          const placementKey = placement as DesignLabPlacementKey;
          const { session } = getPinningSessionForPlacement({
            placementKey,
            designProjectPlacementIds,
            pinningSessions,
            artworkId: placementData.artworkId ? String(placementData.artworkId) : undefined
          });
          const pinningSessionId = isSessionFinished(session?.state)
            ? session!.sessionId
            : undefined;

          return {
            position: position as PlacementArtworkPosition,
            width: placementData.size.width,
            height: placementData.size.height,
            dtfArtwork: {
              id: placementData.artworkId,
              enhancementTier: placementInfo.enhancementTier,
              removeBackground: placementInfo.removeBackground,
              editImageManual: placementInfo.editImageManual || undefined,
              enhancementRequestNote: placementInfo.enhancementRequestNote || undefined,
              jiffyDesignServices: placementInfo.jiffyDesignServices || undefined,
              ...(placementData.llmRedrawRequestId && {
                llmRedrawRequestId: placementData.llmRedrawRequestId.toString()
              }),
              ...(pinningSessionId && { pinningSessionId })
            }
          };
        })
        .filter((placement): placement is NonNullable<typeof placement> => placement !== null);

      const timestamp = Math.floor(Date.now() / 1000);
      const orderNumber = config.orderNumber || "";
      const eventId = `${orderNumber}:${timestamp}:design-lab`;

      const input: AddMadeToCartInput = {
        variants,
        placements: placementsInput,
        sample: isSample,
        eventId,
        designProjectUuid: data.artwork.designProjectUuid
      };

      // Check for zero dimensions in artworks and block submission
      const placementsWithZeroDimensions = placementsInput.filter(
        placement => placement.width <= 0 || placement.height <= 0
      );

      if (placementsWithZeroDimensions.length > 0) {
        // Use console.warn instead of notifyError — this is an expected transient
        // UI condition (dimensions still loading), not a true error.
        // eslint-disable-next-line no-console
        console.warn("Design Lab: Artwork with zero dimensions", {
          input,
          placementsWithZeroDimensions,
          orderNumber,
          eventId
        });
        throw new DimensionLoadingError();
      }

      const result = await addToCart({
        variables: { input },
        errorPolicy: "none" // This will make GraphQL errors throw
      });

      if (!result.data?.addMadeToCart) {
        throw new Error(
          `Failed to add to cart: ${JSON.stringify(input)}; ${JSON.stringify(result)};`
        );
      }

      const response = result.data.addMadeToCart;

      // Update current URL with edit mode params before navigating to cart
      if (response.assemblyGroupId) {
        const compositionId = currentComposition?.id;
        const params = new URLSearchParams();

        params.set("edit", "true");
        params.set("asgid", response.assemblyGroupId.toString());
        params.set("compositionid", compositionId?.toString() || "");

        const newUrl = `/design-lab?${params.toString()}`;

        // Replace current history entry with edit mode URL
        window.history.replaceState(
          {
            assemblyGroupId: response.assemblyGroupId,
            isEditMode: true,
            compositionId: compositionId || ""
          },
          "",
          newUrl
        );
      }

      // Build analytics data
      const analyticsData = buildAddToCartAnalyticsData({
        compositions: data.compositions.addedCompositions,
        products,
        getVariantPrice,
        getCompositionById,
        config,
        eventId
      });

      // Send analytics
      sendAddToCartAnalytics(analyticsData, isSample);

      try {
        // MN-5860: per-call UUID — Meta CAPI dedupes by event_id, so
        // concurrent design-lab add-to-cart successes must not share an id.
        sendServerSideConversion("DesignCompleted", {
          event_id: `DC-${crypto.randomUUID()}`,
          composition_id: currentComposition?.id
        });
      } catch (_) {
        // analytics failure must not break UX
      }

      return {
        assemblyGroupId: response.assemblyGroupId,
        lineItems: response.lineItems.reduce(
          (acc: Record<string, number>, item: { lineItemId: number; quantity: number }) => {
            acc[item.lineItemId.toString()] = item.quantity;
            return acc;
          },
          {} as Record<string, number>
        ),
        blanks: null
      } as models.cart.madeAdd.Response;
    },
    [
      config,
      isSample,
      addToCart,
      products,
      getVariantPrice,
      getCompositionById,
      designProjectPlacementIds,
      pinningSessions,
      currentComposition?.id
    ]
  );

  const handleAddToCart = useCallback(
    async (data: DesignLabFormType) => {
      try {
        const response = await executeAddToCart(data);
        return response;
      } catch (error) {
        if (!(error instanceof DimensionLoadingError)) {
          trackAddToCartError(data, String(error), error);
        }
        throw error;
      }
    },
    [executeAddToCart]
  );

  return {
    placements: PLACEMENTS,
    handleAddToCart
  };
}
